import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiGet } from '../lib/api';
import { CostsCard } from '../components/CostsCard';

type CostSummary = {
  totalCents: number;
  byCategory: Partial<Record<string, number>>;
  windowStart: string;
  windowEnd: string;
};

export function Costs() {
  const { me } = useAuth();
  const tenant = me?.tenants[0];

  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant) return;
    apiGet<CostSummary>(`/api/costs?tenantSlug=${encodeURIComponent(tenant.slug)}&days=30`)
      .then((data) => setSummary(data))
      .catch((e: Error) => setError(e.message));
  }, [tenant]);

  if (!tenant) {
    return (
      <main className="min-h-screen px-6 py-12">
        <div className="mx-auto max-w-3xl">
          <BackLink />
          <section className="mt-8 rounded-lg border border-amber-700/40 bg-amber-900/10 p-6">
            <h2 className="text-lg font-semibold">No workspace yet</h2>
            <p className="mt-2 text-slate-400">
              You need an active workspace to view cost data.{' '}
              <a className="text-sky-400 hover:underline" href="https://clarity.app/signup">
                Start with Pilot — $149/mo
              </a>
              .
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <BackLink />

        <header className="mt-4">
          <p className="text-xs uppercase tracking-widest text-sky-400">{tenant.name}</p>
          <h1 className="mt-1 text-2xl font-semibold">Costs</h1>
          <p className="mt-1 text-slate-400 text-sm">
            Per-tenant cost breakdown — last 30 days.
          </p>
        </header>

        <section className="mt-8">
          {error && (
            <p className="text-red-400 text-sm" role="alert">
              Failed to load cost data: {error}
            </p>
          )}
          {!error && !summary && (
            <p className="text-slate-500 text-sm">Loading…</p>
          )}
          {!error && summary && (
            <CostsCard summary={summary} tenantName={tenant.name} />
          )}
        </section>
      </div>
    </main>
  );
}

function BackLink() {
  return (
    <Link to="/dashboard" className="text-sm text-slate-400 hover:text-slate-200">
      ← Dashboard
    </Link>
  );
}
